/**
 * Lista de cuarentena (T056) — lo que quedó vivo tras un fallo.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { api } from '../../lib/api'

interface Item {
    id: number
    eaCalendarId: string
    errorDetalle: string | null
    inventario: string[]
    resuelta: boolean
    resueltaPor: number | null
    resueltaEn: string | null
}

export function AltaCuarentena() {
    const { data: sesion } = useSesion()
    const qc = useQueryClient()
    const q = useQuery({
        queryKey: ['altas', 'cuarentena'],
        queryFn: () => api.get<{ items: Item[] }>('/altas/cuarentena'),
        enabled: sesion?.role === 'admin',
    })

    const marcar = useMutation({
        mutationFn: (id: number) =>
            api.post(`/altas/${id}/marcar-resuelto`, { confirmar: true }),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ['altas', 'cuarentena'] }),
    })

    const limpiar = useMutation({
        mutationFn: async (id: number) => {
            const prev = await api.post<{
                requiereConfirmacion: boolean
                destruiria: string[]
                encolada: boolean
            }>(`/altas/${id}/reintentar-limpieza`, { confirmar: false })
            if (prev.destruiria.length > 0) {
                const ok = window.confirm(
                    `Esto puede destruir:\n\n${prev.destruiria.join('\n')}\n\n¿Confirmas?`,
                )
                if (!ok) return prev
            }
            return api.post(`/altas/${id}/reintentar-limpieza`, { confirmar: true })
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: ['altas', 'cuarentena'] }),
    })

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    return (
        <div className="mx-auto max-w-2xl space-y-4" data-testid="alta-cuarentena">
            <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                ← Operación
            </Link>
            <h1 className="text-lg font-semibold text-neutral-100">Cuarentena</h1>
            <p className="text-sm text-neutral-400">
                Altas que fallaron y dejaron restos. Nada se borra en silencio.
            </p>
            {q.isPending ? (
                <p className="text-sm text-neutral-500">Cargando…</p>
            ) : (q.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-neutral-500" data-testid="cuarentena-vacia">
                    Vacía
                </p>
            ) : (
                <ul className="space-y-3">
                    {q.data!.items.map((i) => (
                        <li
                            key={i.id}
                            className="border border-neutral-800 p-3 text-sm"
                            data-testid={`cuarentena-${i.id}`}
                        >
                            <div className="flex flex-wrap justify-between gap-2">
                                <Link
                                    to={`/altas/${i.id}`}
                                    className="min-w-0 break-all font-medium text-neutral-100 hover:underline"
                                >
                                    #{i.id} · {i.eaCalendarId}
                                </Link>
                                <span className="text-red-400">en_cuarentena</span>
                            </div>
                            {i.errorDetalle ? (
                                <p className="mt-1 text-xs text-neutral-500">{i.errorDetalle}</p>
                            ) : null}
                            <ul className="mt-2 list-inside list-disc text-xs text-amber-200/90">
                                {i.inventario.map((x) => (
                                    <li key={x}>{x}</li>
                                ))}
                            </ul>
                            <div className="mt-3 flex gap-2">
                                <button
                                    type="button"
                                    className="rounded border border-neutral-700 px-2 py-1 text-xs"
                                    onClick={() => limpiar.mutate(i.id)}
                                >
                                    Reintentar limpieza
                                </button>
                                <button
                                    type="button"
                                    className="rounded border border-neutral-700 px-2 py-1 text-xs"
                                    onClick={() => {
                                        if (window.confirm('¿Marcar resuelta sin borrar el registro?')) {
                                            marcar.mutate(i.id)
                                        }
                                    }}
                                >
                                    Marcar resuelta
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
